import { useState, useRef, useCallback } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Progress,
  Card,
  Checkbox,
} from "@radix-ui/themes";
import { UploadIcon } from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../hooks/useAuth";
import { supabase } from "../../../../lib/supabase";
import type { ImportMeta } from "../../../../lib/import-db";

interface UploadPhaseProps {
  onComplete: (record: ImportMeta) => void;
}

interface WorkerProgress {
  step: string;
  percentage: number;
  error?: string;
}

export function UploadPhase({ onComplete }: UploadPhaseProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<WorkerProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analyseData, setAnalyseData] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const workerRef = useRef<Worker | null>(null);

  const startImport = useCallback(
    async (fileToProcess: File) => {
      if (!fileToProcess || !user) return;

      setIsProcessing(true);
      setError(null);
      setProgress({ step: "Initializing...", percentage: 0 });

      try {
        // Create the worker
        console.log("[UploadPhase] Creating worker...");
        workerRef.current = new Worker(
          new URL("./import.worker.ts", import.meta.url),
          { type: "module" },
        );
        console.log("[UploadPhase] Worker created");

        workerRef.current.onmessage = (event) => {
          console.log("[UploadPhase] Worker message:", event.data);
          const { type, data } = event.data;

          switch (type) {
            case "progress":
              setProgress(data);
              break;
            case "complete":
              setIsProcessing(false);
              setProgress(null);
              onComplete(data.importRecord);
              workerRef.current?.terminate();
              break;
            case "error":
              setIsProcessing(false);
              setError(data.message);
              setProgress(null);
              workerRef.current?.terminate();
              break;
          }
        };

        workerRef.current.onerror = (err) => {
          console.error("[UploadPhase] Worker error:", err);
          setIsProcessing(false);
          setError(`Worker error: ${err.message}`);
          setProgress(null);
        };

        // Read the file and send to worker
        console.log("[UploadPhase] Reading file...");
        const arrayBuffer = await fileToProcess.arrayBuffer();

        // Get the current session access token
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error("No active session");
        }

        console.log("[UploadPhase] Sending message to worker...");
        workerRef.current.postMessage({
          type: "start",
          data: {
            fileBuffer: arrayBuffer,
            userId: user.id,
            supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
            supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            accessToken: session.access_token,
            analyseData,
          },
        });
        console.log("[UploadPhase] Message sent to worker");
      } catch (err) {
        console.error("[UploadPhase] Error:", err);
        setIsProcessing(false);
        setError(
          `Failed to start import: ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
      }
    },
    [user, onComplete, analyseData],
  );

  // Auto-start import when file is selected
  const handleFileSelectAndStart = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setError(null);
        // Trigger import start immediately
        startImport(selectedFile);
      }
    },
    [startImport],
  );

  const handleCancelImport = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    setIsProcessing(false);
    setProgress(null);
  }, []);

  return (
    <Box p="6">
      <Heading size="6" mb="4">
        {t("import.uploadTitle")}
      </Heading>

      <Card style={{ maxWidth: 500 }}>
        <Flex direction="column" gap="4" p="4">
          {!isProcessing ? (
            <>
              <Text size="2" color="gray">
                {t("import.uploadDescription")}
              </Text>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelectAndStart}
                style={{ display: "none" }}
              />

              <Button
                size="3"
                variant="soft"
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon />
                {t("import.selectFile")}
              </Button>

              <Flex asChild gap="2" align="center">
                <Text as="label" size="2">
                  <Checkbox
                    checked={analyseData}
                    onCheckedChange={(checked) =>
                      setAnalyseData(checked === true)
                    }
                  />
                  {t("import.analyseCheckbox")}
                </Text>
              </Flex>
            </>
          ) : (
            <Flex direction="column" gap="3">
              <Text size="2" weight="medium">
                {progress?.step || t("import.processing")}
              </Text>
              <Progress value={progress?.percentage || 0} />
              <Text size="1" color="gray">
                {t("import.percentComplete", {
                  percent: progress?.percentage || 0,
                })}
              </Text>
              <Button
                size="2"
                variant="soft"
                color="gray"
                onClick={handleCancelImport}
              >
                {t("import.cancel")}
              </Button>
            </Flex>
          )}

          {error && (
            <Text size="2" color="red">
              {error}
            </Text>
          )}
        </Flex>
      </Card>
    </Box>
  );
}
